import { logError } from '@/utils/logger';
import { useState, useEffect, useCallback } from 'react';
import { Trash, ArrowCounterClockwise, Warning, Package, Users, UserCircleGear, CurrencyDollar, FileText, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const TYPE_CONFIG = {
  Client: { icon: Users, color: 'text-[#22D3EE]', bg: 'bg-[#22D3EE]/20' },
  Agent: { icon: UserCircleGear, color: 'text-[#2563EB]', bg: 'bg-[#2563EB]/20' },
  Finance: { icon: CurrencyDollar, color: 'text-[#10B981]', bg: 'bg-[#10B981]/20' },
  Rapport: { icon: FileText, color: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]/20' },
  Stock: { icon: Package, color: 'text-[#8B5CF6]', bg: 'bg-[#8B5CF6]/20' },
  'Entrée stock': { icon: Package, color: 'text-[#8B5CF6]', bg: 'bg-[#8B5CF6]/20' },
  Distribution: { icon: Package, color: 'text-[#EC4899]', bg: 'bg-[#EC4899]/20' },
};

const Corbeille = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchTrash = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trash`, { credentials: 'include' });
      if (res.ok) setItems(await res.json());
    } catch (error) { logError('Fetch trash:', error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTrash(); }, [fetchTrash]);

  const handleRestore = async (trashId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trash/restore/${trashId}`, { method: 'POST', credentials: 'include' });
      if (res.ok) { toast.success('Élément restauré'); fetchTrash(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const handlePermanentDelete = async (trashId) => {
    if (!window.confirm('Supprimer DÉFINITIVEMENT ? Cette action est irréversible.')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trash/${trashId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Supprimé définitivement'); fetchTrash(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const handleEmptyTrash = async () => {
    if (!window.confirm('Vider toute la corbeille ? Tous les éléments seront supprimés DÉFINITIVEMENT.')) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trash/empty/all`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { const data = await res.json(); toast.success(data.message); fetchTrash(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; } };

  if (loading) return <div className="text-[#94A3B8]">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight">Corbeille</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">{items.length} élément(s) supprimé(s)</p>
        </div>
        {items.length > 0 && (
          <Button onClick={handleEmptyTrash} variant="outline" className="border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444] hover:text-white" data-testid="empty-trash-button">
            <Trash size={18} className="mr-2" /> Vider la corbeille
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-16 text-center">
          <Trash size={48} className="mx-auto text-[#334155] mb-4" weight="duotone" />
          <p className="text-[#94A3B8] text-lg">La corbeille est vide</p>
          <p className="text-[#64748B] text-sm mt-1">Les éléments supprimés apparaîtront ici</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou type..."
              className="w-full bg-[#0F172A] border border-[#1E293B] rounded-md pl-9 pr-3 py-2.5 text-sm text-[#F8FAFC] placeholder-[#64748B] focus:outline-none focus:border-[#2563EB]"
              data-testid="search-trash"
            />
          </div>
          {items.filter(i => !search || i.item_name?.toLowerCase().includes(search.toLowerCase()) || i.item_type?.toLowerCase().includes(search.toLowerCase())).map(item => {
            const config = TYPE_CONFIG[item.item_type] || { icon: Package, color: 'text-[#94A3B8]', bg: 'bg-[#94A3B8]/20' };
            const Icon = config.icon;
            return (
              <div key={item.trash_id} className="bg-[#0F172A] border border-[#1E293B] rounded-md p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:border-[#334155] transition-colors" data-testid={`trash-item-${item.trash_id}`}>
                <div className={`h-10 w-10 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon size={20} className={config.color} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[#F8FAFC] font-medium truncate">{item.item_name}</span>
                    <Badge className={`${config.bg} ${config.color} border-0 text-xs`}>{item.item_type}</Badge>
                  </div>
                  <p className="text-xs text-[#64748B] mt-0.5">Supprimé le {fmtDate(item.deleted_at)}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                  <Button size="sm" onClick={() => handleRestore(item.trash_id)} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid={`restore-${item.trash_id}`} title="Restaurer">
                    <ArrowCounterClockwise size={16} className="mr-1" /> Restaurer
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handlePermanentDelete(item.trash_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444]" data-testid={`perm-delete-${item.trash_id}`} title="Supprimer définitivement">
                    <Warning size={16} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Corbeille;
